/**
 * The pages that exist only for a signed-in customer.
 *
 * WHY THIS LIST EXISTS
 * --------------------
 * When a background API call answers 401, the API client (Config/Api.js) has
 * to choose between two things: send the visitor to /login, or clear the stale
 * token quietly and leave them where they are. Sending them to /login is right
 * only on a page that has nothing to show without an account. Everywhere else
 * — a shop, a product, the checkout, a confirmation, a stay, a course, the
 * booking flow — it throws a shopper out of a purchase, and those pages ask for
 * a login themselves (the checkout's and the booking flow's login popup) at the
 * moment the shopper acts.
 *
 * The client used to decide with a list of EXCEPTIONS (pages that must not
 * redirect). Every page nobody had thought to list redirected — which on
 * 2026-09-16 was `/:slug/checkout` on every phone with Apple Pay. This list is
 * the other way round: public is the default, and a page redirects only when it
 * is named here. A page nobody listed can never throw a shopper out.
 *
 * WHERE IT COMES FROM
 * -------------------
 * The router (src/App.js). These are the customer-account routes, which are
 * slug-less in BOTH route trees — the platform (`stylon.online/...`) and a
 * business's own domain — so one list covers both:
 *
 *   - every route `AuthProtection` actually gates (its PROTECTED_ROUTES:
 *     /home, /profile/*, /orders/:id, /appointments, /booking);
 *   - the customer's own records the router renders without that gate, each of
 *     which is only that customer's data: /stays/:id (StayDetails — note the
 *     public stays LIST is `/stays` on a custom domain, which is NOT here),
 *     /see-all-appointments, /appointment-details/:id (which already sends a
 *     signed-out visitor to /login itself), and /customer-onboard (needs the
 *     onboarding token AuthProtection checks).
 *
 * src/utils/__tests__/everyRouteIsAnAccountPageOrAPublicOne.test.js reads every
 * `<Route path>` in App.js and fails when one is added without a decision here.
 */

export const ACCOUNT_PAGES = [
  '/home',
  '/profile',
  '/profile/*',
  '/orders/:id',
  '/stays/:id',
  '/appointments',
  '/booking',
  '/see-all-appointments',
  '/appointment-details/:id',
  '/customer-onboard',
];

const segments = (path) => String(path || '')
  .split('?')[0]
  .split('#')[0]
  .toLowerCase()
  .split('/')
  .filter(Boolean);

/** Whether `pathname` is an address of the route `pattern` (`:param`, trailing `*`). */
export function pathMatches(pattern, pathname) {
  const want = segments(pattern);
  const got = segments(pathname);
  for (let i = 0; i < want.length; i += 1) {
    if (want[i] === '*') return got.length > i; // `/profile/*` is a page under /profile
    if (i >= got.length) return false;
    if (want[i].startsWith(':')) continue;
    if (want[i] !== got[i]) return false;
  }
  return got.length === want.length;
}

/** True when `pathname` is one of the customer's own pages (see above). */
export function isAccountPage(pathname) {
  return ACCOUNT_PAGES.some((pattern) => pathMatches(pattern, pathname));
}
